<template>
    <div
        :key="product.id"
        v-if="list.length > 0"
        id="product-images-carousel"
        class="carousel slide position-relative pointer-event"
        data-bs-ride="carousel"
    >
        <div class="position-absolute top-2 end-0 mt-0 m-2 z-3">
            <WatchlistButton :product_id="product.id" />
        </div>
        <div v-if="list.length > 1" class="carousel-indicators dots">
            <button
                v-for="(_, idx) in list"
                :key="idx"
                type="button"
                data-bs-target="#product-images-carousel"
                :data-bs-slide-to="idx"
                :class="{ active: idx === 0 }"
                :aria-current="idx === 0 ? 'true' : null"
                :aria-label="`Slide ${idx + 1}`"
            />
        </div>

        <div class="carousel-inner">
            <div
                v-for="(item, index) in list"
                :key="index"
                class="carousel-item"
                :class="{ active: index === 0 }"
            >
                <div v-if="item.video" class="carousel-item-inner">
                    <iframe
                        width="100%"
                        height="100%"
                        :src="getYouTubeEmbedUrl(item.link)"
                        :id="'youtube-player-' + index"
                        frameborder="0"
                        allow="autoplay; encrypted-media"
                        allowfullscreen
                    ></iframe>
                </div>
                <div
                    v-else
                    class="carousel-item-inner"
                    v-html="item"
                    :ref="el => picContainers[index] = el"
                ></div>
            </div>
        </div>

        <template v-if="list.length > 1 && !playingVideo">
            <button
                class="carousel-control-prev"
                type="button"
                data-bs-target="#product-images-carousel"
                data-bs-slide="prev"
            >
                <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                <span class="visually-hidden">{{ $t('Previous') }}</span>
            </button>
            <button
                class="carousel-control-next"
                type="button"
                data-bs-target="#product-images-carousel"
                data-bs-slide="next"
            >
                <span class="carousel-control-next-icon" aria-hidden="true"></span>
                <span class="visually-hidden">{{ $t('Next') }}</span>
            </button>
        </template>
    </div>

    <div v-else class="me-xl-4 w-100">
        <div class="text-center py-4">
            <span v-if="product.images.fallback" v-html="product.images.fallback" />
        </div>
    </div>
</template>

<script setup>
import { useNuxtApp } from '#app'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import PhotoSwipe from 'photoswipe'
import 'photoswipe/style.css'
const WatchlistButton = defineAsyncComponent(() => import('~/components/Product/Buttons/WatchlistButton.vue'))

const props = defineProps({
    product: { type: Object, required: true }
})
const { $bootstrap } = useNuxtApp()
const list = props.product.images.list
const youtubePlayers = {}
const playingVideo = ref(false)
let picContainers = []

const pauseCarousel = () => {
    const el = document.getElementById('product-images-carousel')
    const inst = el && $bootstrap.Carousel.getInstance(el)
    inst && inst.pause()
}

const resumeCarousel = () => {
    const el = document.getElementById('product-images-carousel')
    const inst = el && $bootstrap.Carousel.getInstance(el)
    inst && inst.cycle()
}

const initializeYouTubePlayers = () => {
    list.forEach((item, idx) => {
        if (item.video) {
            youtubePlayers[idx] = new YT.Player(`youtube-player-${idx}`, {
                events: {
                    onStateChange: e => {
                        if (e.data === YT.PlayerState.PLAYING) {
                            playingVideo.value = true
                            pauseCarousel()
                        } else if (
                            e.data === YT.PlayerState.ENDED ||
                            e.data === YT.PlayerState.PAUSED
                        ) {
                            playingVideo.value = false
                            resumeCarousel()
                        }
                    }
                }
            })
        }
    })
}

const getYouTubeEmbedUrl = url => {
    const m = url.match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|.*[?&]v=))([^&?#]+)/
    )
    return m
        ? `https://www.youtube.com/embed/${m[1]}?enablejsapi=1&controls=0&fs=0`
        : ''
}

const baseArrow = `
<svg xmlns="http://www.w3.org/2000/svg"
     width="24" height="24"
     viewBox="0 0 16 16"
     fill="currentColor">
  <g transform="rotate(90 8 8)">
    <path fill-rule="evenodd"
          d="M1.646 4.646a.5.5 0 0 1 .708 0L8
             10.293l5.646-5.647a.5.5 0 1
             1 .708.708l-6 6a.5.5 0 0
             1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
  </g>
</svg>`;

let lightboxInstance

onMounted(async () => {
    await nextTick();

    const loadPromises = picContainers.map(container => {
        const img = container.querySelector('img');
        if (!img) return Promise.resolve(null);
        if (img.complete && img.naturalWidth) return Promise.resolve(img);

        return new Promise(resolve => {
            img.addEventListener('load', () => resolve(img), { once: true });
        });
    });

    const imgs = (await Promise.all(loadPromises)).filter(Boolean);
    imgs.forEach(img => {
        const src = img.currentSrc || img.src;
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const picture = img.closest('picture') || img;
        const a = document.createElement('a');
        a.setAttribute('data-pswp-src', src);
        a.setAttribute('data-pswp-width', w);
        a.setAttribute('data-pswp-height', h);
        picture.parentNode.replaceChild(a, picture);
        a.appendChild(picture);
    });

    lightboxInstance = new PhotoSwipeLightbox({
        gallery: '#product-images-carousel',
        children: 'a[data-pswp-src]',
        imageClickAction: 'close',
        // Use a function to set zoom level based on screen size
        initialZoomLevel: (zoomLevelObject) => {
            // On mobile, fit the image to the screen.
            if (window.innerWidth < 768) {
                return zoomLevelObject.fit;
            }
            // On desktop, use 2x zoom.
            return 2;
        },
        secondaryZoomLevel: 2,
        mainClass: 'pswp-bg',
        bgOpacity: 1,
        zoom: false,
        pswpModule: PhotoSwipe,
        arrowPrevSVG: baseArrow.replace(
            '<g ',
            `<g transform="rotate(90 8 8) "`
        ),
        arrowNextSVG: baseArrow.replace(
            '<g ',
            `<g transform="rotate(-90 8 8) "`
        ),
    });
    lightboxInstance.init();

    const el = document.getElementById('product-images-carousel');
    if (el) {
        $bootstrap.Carousel.getOrCreateInstance(el, { touch: true, interval: 5000 });
    }

    if (!window.YT) {
        const tag = document.createElement('script')
        tag.src = 'https://www.youtube.com/iframe_api'
        tag.async = true
        document.head.appendChild(tag)
        window.onYouTubeIframeAPIReady = initializeYouTubePlayers
    } else {
        initializeYouTubePlayers()
    }
});

onUnmounted(() => {
    lightboxInstance?.destroy();
})
</script>

<style scoped>
:deep(.carousel-item-inner a[data-pswp-src]) {
    cursor: zoom-in;
    display: block;
}
</style>
